import type { ReactNode } from 'react';
import { Formula, isolateNotation } from './scientific.js';

/**
 * Calculated results of a specialised form (D328): one block per value, in a
 * grid of equal cells, on the calculated tint — never an input box, so a value
 * the system worked out never looks like one a person typed (CLAUDE.md
 * section 8). Presentation only.
 */

/**
 * One calculated value, read-only: its label (the name and symbol), the value
 * (a `Reading` or `Quantity` from the template's own formatter), and the
 * formula, when the template passes one, as small secondary text. In a row of
 * blocks the labels share one area (`.aligned-label`), so the values line up.
 */
export function ResultBlock({
  label,
  formula,
  children,
  testId,
}: {
  readonly label: string;
  /** Only ever a formula the template already implements and words. */
  readonly formula?: string;
  readonly children: ReactNode;
  readonly testId?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-medium bg-white px-3 py-2" data-testid="calculated-cell" data-result-block="">
      <dt className="aligned-label text-caption text-calc-ink">{isolateNotation(label)}</dt>
      <dd dir="ltr" className="text-start" {...(testId ? { 'data-testid': testId } : {})}>
        {children}
      </dd>
      {formula ? <Formula>{formula}</Formula> : null}
    </div>
  );
}

/**
 * Result blocks side by side, ALL EQUAL WIDTH: as many columns of at least
 * `minWidth` as fit, the tracks stretched to fill the row (`auto-fit`), one
 * column on a phone. `surface="card"` is the band under a determination card's
 * readings; `surface="panel"` a free-standing strip (a summary, key results).
 */
export function ResultGrid({
  children,
  surface = 'panel',
  className,
  testId,
  source,
}: {
  readonly children: ReactNode;
  readonly surface?: 'card' | 'panel';
  readonly className?: string;
  readonly testId?: string;
  readonly source?: string;
}) {
  const shape =
    surface === 'card'
      ? 'grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] border-t border-dashed border-default-300'
      : 'grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] rounded-large';
  return (
    <dl
      className={`aligned-fields grid gap-2 bg-calc p-2.5 ${shape}${className ? ` ${className}` : ''}`}
      data-result-grid=""
      {...(testId ? { 'data-testid': testId } : {})}
      {...(source ? { 'data-source': source } : {})}
    >
      {children}
    </dl>
  );
}

/** The summary under the cards — averages, totals: calculated, never typed. */
export function SummaryResult({
  children,
  testId,
  source,
}: {
  readonly children: ReactNode;
  readonly testId?: string;
  readonly source?: string;
}) {
  return (
    <ResultGrid
      className="mt-stack"
      {...(testId ? { testId } : {})}
      {...(source ? { source } : {})}
    >
      {children}
    </ResultGrid>
  );
}
